import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { FileDownload } from '@mui/icons-material';
import { getUsers, getNegotiationPending, getNegotiationCompleted, getPendingInfluencers, getAssignedInfluencers, getCompletedInfluencers, getNegotiationInProgress } from '../services/api';
import * as XLSX from 'xlsx';

function Report() {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const [usersResponse, pendingResponse, completedResponse, pendingInfluencersResponse, assignedInfluencersResponse, completedInfluencersResponse, negotiationInProgress] = await Promise.all([
        getUsers(),
        getNegotiationPending(),
        getNegotiationCompleted(),
        getPendingInfluencers(),
        getAssignedInfluencers(),
        getCompletedInfluencers(),
        getNegotiationInProgress()
      ]);

      const users = usersResponse.data.filter(user => user.role === 'onboarder');
      const pendingInfluencers = pendingResponse.data.data;
      const inProgressInfluencers = negotiationInProgress.data.data;
      const completedInfluencers = completedResponse.data.data;
      const pendingExistingInfluencers = pendingInfluencersResponse.data?.data || [];
      const assignedExistingInfluencers = assignedInfluencersResponse.data?.data || [];
      const completedExistingInfluencers = completedInfluencersResponse.data?.data || [];

      const reportRows = users.map((user, index) => {
        const userPending = pendingInfluencers.filter(inf => inf.onboarderId?._id === user._id);
        const userNegotiationInProgress = inProgressInfluencers.filter(inf => inf.onboarderId?._id === user._id);
        const userCompleted = completedInfluencers.filter(inf => inf.onboarderId?._id === user._id);

        const userAssigned = assignedExistingInfluencers.filter(inf => inf.assignedTo?._id === user._id);
        const userCompletedExisting = completedExistingInfluencers.filter(inf => inf.assignedTo?._id === user._id);

        const instaSavings = userCompleted.reduce((sum, inf) => {
          const reelCost = Number(inf.reelCost) || 0;
          const negotiatedCost = Number(inf.negotiationCostData?.reelCost) || 0;
          if (reelCost === 0) return sum;
          return sum + ((reelCost - negotiatedCost) / reelCost) * 100;
        }, 0);

        const youtubeSavings = userCompleted.reduce((sum, inf) => {
          const originalTotal = (Number(inf.youtubeShortsCost) || 0) + (Number(inf.youtubeVideoCost) || 0);
          const negotiatedTotal = (Number(inf.negotiationCostData?.youtubeShortsCoast) || 0) + (Number(inf.negotiationCostData?.youtubeVideoCost) || 0);
          if (originalTotal === 0) return sum;
          return sum + ((originalTotal - negotiatedTotal) / originalTotal) * 100;
        }, 0);

        return {
          id: user._id,
          sno: index + 1,
          onboarderName: user.name || user.username,
          onboardingFormsSubmitted: userPending.length + userNegotiationInProgress.length + userCompleted.length,
          assignedCount: userAssigned.length + userCompletedExisting.length,
          assignedPending: userAssigned.length,
          assignedCompleted: userCompletedExisting.length,
          negotiatedPending: userPending.length,
          negotiatedCompleted: userCompleted.length,
          instaSavingsPercent: userCompleted.length > 0 ? Math.round(instaSavings / userCompleted.length).toString() : '0',
          youtubeSavingsPercent: userCompleted.length > 0 ? (youtubeSavings / userCompleted.length).toFixed(2) : '0.00'
        };
      });

      setReportData(reportRows);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTotalRow = () => {
    const totals = reportData.reduce((acc, row) => ({
      onboardingFormsSubmitted: acc.onboardingFormsSubmitted + row.onboardingFormsSubmitted,
      negotiatedPending: acc.negotiatedPending + row.negotiatedPending,
      negotiatedCompleted: acc.negotiatedCompleted + row.negotiatedCompleted,
      instaSavingsSum: acc.instaSavingsSum + parseFloat(row.instaSavingsPercent)
    }), { onboardingFormsSubmitted: 0, negotiatedPending: 0, negotiatedCompleted: 0, instaSavingsSum: 0 });

    return {
      id: 'total',
      onboarderName: 'Total',
      onboardingFormsSubmitted: totals.onboardingFormsSubmitted,
      negotiatedPending: totals.negotiatedPending,
      negotiatedCompleted: totals.negotiatedCompleted,
      instaSavingsPercent: Math.round(totals.instaSavingsSum).toString()
    };
  };

  const exportToExcel = () => {
    try {
      const data = reportData.map(row => ({
        'SNO': row.sno,
        'Onboarder Name': row.onboarderName,
        'Onboarding Forms Submitted Count': row.onboardingFormsSubmitted,
        // 'Assigned Count': row.assignedCount,
        // 'Assigned - Pending': row.assignedPending,
        // 'Assigned - Completed': row.assignedCompleted,
        'Negotiated - Pending': row.negotiatedPending,
        'Negotiated - Completed': row.negotiatedCompleted,
        'Overall Negotiated Savings %(Insta)': `${row.instaSavingsPercent}%`,
        // 'Overall Negotiated Savings %(YouTube)': `${row.youtubeSavingsPercent}%`
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report Data');
      XLSX.writeFile(workbook, 'report_data.xlsx');
    } catch (error) {
      console.error('Failed to export data:', error);
    }
  };

  const columns = [
    { field: 'sno', headerName: 'S.No', width: 80, headerTooltip: 'Serial Number' },
    { field: 'onboarderName', headerName: 'Onboarder Name', flex: 1, minWidth: 150, headerTooltip: 'Onboarder Name' },
    { field: 'onboardingFormsSubmitted', headerName: 'Onboarding Forms Submitted Count', flex: 1, minWidth: 200, headerTooltip: 'Onboarding Forms Submitted Count' },
    // { field: 'assignedCount', headerName: 'Assigned Count', flex: 0.8, minWidth: 120, headerTooltip: 'Assigned Count' },
    // { field: 'assignedPending', headerName: 'Assigned - Pending', flex: 0.8, minWidth: 130, headerTooltip: 'Assigned - Pending' },
    // { field: 'assignedCompleted', headerName: 'Assigned - Completed', flex: 0.8, minWidth: 140, headerTooltip: 'Assigned - Completed' },
    { field: 'negotiatedPending', headerName: 'Negotiated - Pending', flex: 0.8, minWidth: 140, headerTooltip: 'Negotiated - Pending' },
    { field: 'negotiatedCompleted', headerName: 'Negotiated - Completed', flex: 0.8, minWidth: 160, headerTooltip: 'Negotiated - Completed' },
    {
      field: 'instaSavingsPercent',
      headerName: 'Overall Negotiated Savings %(Insta)',
      flex: 1,
      minWidth: 200,
      headerTooltip: 'Overall Negotiated Savings %(Insta)',
      renderCell: (params) => `${params.value}%`
    },
    // { 
    //   field: 'youtubeSavingsPercent', 
    //   headerName: 'Overall Negotiated Savings %(YouTube)', 
    //   flex: 1, 
    //   minWidth: 220,
    //   headerTooltip: 'Overall Negotiated Savings %(YouTube)',
    //   renderCell: (params) => `${params.value}%`
    // }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Report
        </Typography>
        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={exportToExcel}
        >
          Export Excel
        </Button>
      </Box>

      <DataGrid
        columns={columns}
        rows={[...reportData, getTotalRow()]}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        getRowClassName={(params) => params.id === 'total' ? 'total-row' : ''}
        sx={{
          boxShadow: 2,
          backgroundColor: 'white',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f8f9fa'
          },
          '& .MuiDataGrid-iconSeparator': {
            display: 'none'
          },
          '& .MuiDataGrid-row': {
            backgroundColor: 'white',
            borderLeft: 'solid 1px #e5e7eb',
            borderRight: 'solid 1px #e5e7eb'
          },
          '& .total-row': {
            backgroundColor: '#f0f7ff',
            fontWeight: 'bold',
            '& .MuiDataGrid-cell': {
              fontWeight: 'bold'
            }
          }
        }}
      />
    </Box>
  );
}

export default Report;